import { apiFetch } from '../../../lib/api/client'
import { useBookingStore } from '../../../store'
import type {
  PreAppointmentPurposeData,
  UpdateAppointmentPurposePayload,
} from '../PreAppointmentPurpose.types'

/**
 * Fetches purpose options for the post-booking preferences screen.
 * Cached on the booking store so the screen can render without a refetch.
 */
export async function getPreAppointmentPurpose(): Promise<PreAppointmentPurposeData> {
  const { language, setState } = useBookingStore.getState()
  const data = await apiFetch<PreAppointmentPurposeData>(
    `v2/booking/appointment-purpose-options/?language=${encodeURIComponent(language)}`,
  )
  setState({ preAppointmentPurpose: data })
  return data
}

/**
 * Updates the booking with the customer's purpose selection / notes.
 * `visitId` is the booking id created on the user-data step.
 */
export async function submitPreAppointmentPurpose(
  preferences: Omit<UpdateAppointmentPurposePayload, 'visitId'> & {
    visitId?: string
  },
): Promise<{ status: string; message?: string }> {
  const { bookingId } = useBookingStore.getState()
  const visitId = preferences.visitId ?? bookingId

  if (!visitId) {
    throw new Error('Cannot submit appointment purpose without a booking id')
  }

  const data = await apiFetch<{ status: string; message?: string }>(
    'v2/booking/update-appointment-purpose/',
    {
      method: 'POST',
      body: JSON.stringify({ ...preferences, visitId }),
    },
  )

  if (data.status !== 'success') {
    throw new Error(data.message ?? 'Failed to submit pre-appointment purpose')
  }

  return data
}
